/**
 * Per-subsystem configuration — model routing, tool policies, external MCP,
 * subscription runtime, infra credentials, scheduler, agent loop, browser
 * pool, retrieval, and tool-I/O. Each block pairs an interface with its
 * DEFAULT_* and resolve*() clamp. Aggregated by OnestoneConfig in ./config-root.
 */
import { defaultsOf, resolveSection, type SectionValues } from '../settings/registry.js';
import {
  AGENT_SETTINGS,
  BROWSER_SETTINGS,
  MCP_CLIENT_SETTINGS,
  RETRIEVAL_SETTINGS,
  SCHEDULER_SETTINGS,
  TOOL_IO_SETTINGS,
} from '../settings/sections.js';
import type { EffortLevel } from '../models.js';

/**
 * One channel's whole model setup (Settings → Model) — `config.channels[ch]`,
 * read through `resolveChannelExecution` (channels.ts) and changed through
 * `PATCH /api/config/channels/:channel`. A field left out is the default.
 * What a model is actually sent for effort, temperature and max output is
 * decided by `resolveModelParams` (modelParams.ts).
 */
export interface ChannelSettings {
  /**
   * Where the channel's models run: `'subscription'` (the Claude plan through
   * the Claude Code CLI), `'api'` (the Anthropic API) or a provider id
   * (`'groq'`, `'ollama'`, ...). Unset → the subscription when it is the
   * default (`claudeCode.authMode`), else the Anthropic API. The transport is
   * derived from it, never stored.
   */
  source?: string | undefined;
  /**
   * The channel's model — a model of its source. No fallback: an unset channel
   * fails with "no model selected" rather than silently running a hardcoded
   * model that goes stale as new versions ship.
   */
  model?: string | undefined;
  effort?: EffortLevel | undefined;
  temperature?: number | undefined;
  /** Most output tokens per reply; unset → the model-aware ceiling. */
  maxTokens?: number | undefined;
  /** Daily spending guardrail. */
  budget?: ChannelBudget | undefined;
  /** Tried in order when the channel's provider fails transiently (5xx / 429 / timeout). */
  fallbacks?: ChannelFallbackEntry[] | undefined;
}

/**
 * Daily spending guardrail for a channel (`decideChannelBudget`). Before a
 * turn, today's pay-per-token spend on the channel is summed; past
 * `downgradeAt`% of `dailyUsd` the turn runs `fallbackModel`, and at 100% new
 * turns on the channel are refused until midnight or the cap is raised.
 */
export interface ChannelBudget {
  /** Hard daily cap in USD. 0 or undefined disables the budget for the channel. */
  dailyUsd?: number | undefined;
  /** Percentage of dailyUsd at which to auto-downgrade (default 80). */
  downgradeAt?: number | undefined;
  /** Model to fall back to when over the downgrade threshold. */
  fallbackModel?: string | undefined;
}

/**
 * One step in a cross-provider fallback chain. On transient provider errors
 * the agent loop attempts the next entry. Effort is per-entry because the
 * primary may want xhigh while a Groq fallback can't accept effort at all.
 */
export interface ChannelFallbackEntry {
  /** Provider id ('anthropic' | 'subscription' | 'groq' | 'ollama' | ...). */
  provider: string;
  model: string;
  effort?: EffortLevel | undefined;
}

export interface ToolPolicy {
  enabled: boolean;
  timeoutMs?: number;
  maxChars?: number;
  /**
   * Per-tool approval requirement. When true, a call to this tool surfaces an
   * inline Accept/Deny card and waits (reusing the ask_operator/QuestionRegistry
   * path). READ + ENFORCED by the unified agency gate (MA-350) — a per-tool
   * floor the agency level sits above. Configured via Settings → Tools; when
   * unset, the tool record's default applies (`ToolCatalogFields.requireApproval`).
   */
  requireApproval?: boolean;
}

/**
 * Bash safety posture (Settings → Tools). Single source of truth — the
 * enforcement module (`src/agent/tools/policy.ts`) and the ToolsPanel UI import
 * this rather than redeclaring it.
 * - `off` (default): fully-autonomous bash.
 * - `block-dangerous`: deny commands the risk classifier flags.
 * - `approve-dangerous`: dangerous commands surface an inline Accept/Deny card.
 * - `approve-all`: every command requires operator approval.
 */
export const BASH_SAFETY_MODES = ['off', 'block-dangerous', 'approve-dangerous', 'approve-all'] as const;
export type BashSafety = (typeof BASH_SAFETY_MODES)[number];

/**
 * Unified agent-agency level (MA-350) — the single control governing how much
 * the INTERACTIVE agent may do without operator approval. Enforced at the tool
 * dispatch interceptor (`src/agent/tools/policy.ts` → `assertAgencyAllowed`).
 * - `full` (default): fully autonomous — every tool runs (today's behavior).
 * - `guided`: any tool that can change something (`mutates` — file edits, shell,
 *   tests, store writes, sends, deletes; or per-tool `requireApproval`) surfaces
 *   an inline Accept/Deny card; reads run free.
 * - `manual`: every tool call requires operator approval.
 *
 * SCOPE: the level governs INTERACTIVE (dashboard) turns only. Routines and
 * sub-agents run headless (no operator to approve) and are exempt from the
 * approval requirement — they are pre-authorized by being scheduled/spawned.
 * The `deny` list is the UNIVERSAL safety floor: it blocks matching tools for
 * EVERY caller (interactive, routine, sub-agent) at every level, and the agent
 * can never lower it (user-only, per OWASP Agentic ASI10 / least-agency).
 */
export const AGENCY_LEVELS = ['full', 'guided', 'manual'] as const;
export type AgencyLevel = (typeof AGENCY_LEVELS)[number];

export interface AgencyConfig {
  /** Interactive-agent autonomy level. Default 'full'. */
  level?: AgencyLevel;
  /** Universal deny-list — tool names blocked for ALL callers at ALL levels. */
  deny?: string[];
}

/**
 * External MCP server declaration — spawned as a subprocess (stdio) or
 * connected via Streamable HTTP. Tools are auto-discovered on connect.
 *
 * Transport inference: `url` present → http; `command` present → stdio.
 * Explicit `transport` field takes precedence if set.
 */
export interface ExternalMcpServerConfig {
  name: string;
  /** Transport type. Inferred from url/command if omitted. */
  transport?: 'stdio' | 'http' | undefined;
  // ── Stdio transport ──
  /** Command to spawn (stdio mode). */
  command?: string | undefined;
  /** Arguments for the command (stdio mode). */
  args?: string[] | undefined;
  /** Additional environment variables (stdio mode). */
  env?: Record<string, string> | undefined;
  // ── HTTP transport ──
  /** MCP endpoint URL (http mode), e.g. "https://mcp.example.com/mcp". */
  url?: string | undefined;
  /** Custom HTTP headers (http mode), e.g. {"Authorization": "Bearer <token>"}. */
  headers?: Record<string, string> | undefined;
  // ── Common ──
  enabled?: boolean | undefined;
  /**
   * The operator trusts this server's tool annotations (ADR-0006). Off by
   * default: an untrusted server's tools all count as able to change things —
   * approval under `guided`, never on a sub-agent's surface — whatever their
   * annotations claim. Trusted: a tool is read-only when it declares
   * `readOnlyHint: true`. Set only in the dashboard.
   */
  trusted?: boolean | undefined;
}

/**
 * Where an external MCP server's connection stands: `connecting` (first try),
 * `connected`, or `retrying` (it dropped or failed; another try is scheduled).
 */
export const EXTERNAL_MCP_STATES = ['connecting', 'connected', 'retrying'] as const;
export type ExternalMcpState = (typeof EXTERNAL_MCP_STATES)[number];

/** One enabled external MCP server's live status (ADR-0006) — dashboard, `external_mcp_status`, health. */
export interface ExternalMcpLiveStatus {
  name: string;
  state: ExternalMcpState;
  transport: 'stdio' | 'http';
  trusted: boolean;
  /** The protocol revision the connection negotiated (e.g. `2026-07-28`, `2025-11-25`). */
  protocolVersion?: string | undefined;
  toolCount: number;
  tools: string[];
  /** Length of the server's own instructions as sent (the agent gets at most `mcpClient.instructionsMaxChars`). */
  instructionsChars: number;
  /** Why the last try failed or the connection dropped. */
  lastError?: string | undefined;
  /** Failed tries since the last good connection. */
  failures: number;
  /** When the next try runs (state `retrying`). */
  nextRetryAt?: string | undefined;
  connectedAt?: string | undefined;
}

// ---------------------------------------------------------------------------
// Subscription runtime — CLI-backed execution via the operator's monthly plan
// ---------------------------------------------------------------------------

/** Which backend serves Anthropic models: direct API key vs. subscription plan. */
export const CLAUDE_AUTH_MODES = ['api-key', 'subscription'] as const;
export type ClaudeAuthMode = (typeof CLAUDE_AUTH_MODES)[number];

/** `--permission-mode` passed to headless CLI runs. */
export const CLAUDE_PERMISSION_MODES = ['default', 'acceptEdits', 'bypassPermissions', 'plan'] as const;
export type ClaudePermissionMode = (typeof CLAUDE_PERMISSION_MODES)[number];

/**
 * Settings for CLI-backed execution using the operator's subscription plan.
 * The `oauthToken` is stored as an encrypted envelope (see storage/secretCodec).
 * All fields are dashboard-configurable via `/api/claude`.
 */
export interface ClaudeCodeConfig {
  /** Which Anthropic backend Claude models use. Default: 'api-key'. */
  authMode?: ClaudeAuthMode | undefined;
  /** Claude Code CLI command/path (default 'claude'). */
  command?: string | undefined;
  /** Permission mode for headless runs. Default 'default'. */
  permissionMode?: ClaudePermissionMode | undefined;
  /** Encrypted CLAUDE_CODE_OAUTH_TOKEN envelope. Never returned to the renderer. */
  oauthToken?: string | undefined;
}

/**
 * Non-secret view of which Claude account OneStone is connected to. Populated on
 * the dashboard status payload so the connected card can show whose
 * subscription + plan OneStone is using (we never see/expose the email — the OAuth
 * token doesn't carry it). `source` distinguishes between the borrowed CLI
 * credentials (symlink to `~/.claude/.credentials.json`) and an OneStone-stored
 * token (encrypted in `~/.onestone/config/`).
 */
export interface ClaudeAccountInfo {
  /** Where OneStone is reading the auth from. */
  source: 'cli-symlink' | 'onestone-token';
  /** Subscription tier as reported by the credentials file (e.g. 'Max 20x'), or null when unknown. */
  subscriptionType: string | null;
  /** OAuth access-token expiry (epoch ms), or null when unknown. Refresh is silent. */
  expiresAt: number | null;
}

// ---------------------------------------------------------------------------
// Cloud link (desktop ↔ cloud account)
// ---------------------------------------------------------------------------

/** The desktop's cloud link, written by the link and sync routes. */
export interface CloudLinkConfig {
  /** Sealed device session token; present while linked. */
  sessionToken?: string | undefined;
  linkedAt?: string | undefined;
  email?: string | undefined;
  lastSyncedAt?: string | undefined;
  lastSync?: { pushed: number; pulled: number; conflicts: number; cursor: number } | undefined;
}

// ---------------------------------------------------------------------------
// Infrastructure credentials (Cloudflare, Porkbun)
// ---------------------------------------------------------------------------

export interface InfraConfig {
  cloudflare?: {
    /** Encrypted Cloudflare API Token (Bearer auth). */
    apiToken: string;
    /** Cloudflare Account ID — public identifier, stored plaintext (not a secret). */
    accountId?: string;
  };
  porkbun?: {
    /** Encrypted Porkbun API key (pk_...). */
    apikey: string;
    /** Encrypted Porkbun secret API key (sk_...). */
    secretapikey: string;
  };
  github?: {
    /** Encrypted GitHub Personal Access Token (Bearer auth). */
    token: string;
  };
}

// ---------------------------------------------------------------------------
// Tunable sections — declared once in the settings registry
// (../settings/sections.ts); types, defaults and readers are derived from it.
// ---------------------------------------------------------------------------

/** Routine engine settings (`scheduler`). catchUpWindowMs is stored in ms; Settings shows hours. */
export type SchedulerConfig = SectionValues<typeof SCHEDULER_SETTINGS>;
export const DEFAULT_SCHEDULER_CONFIG: SchedulerConfig = defaultsOf(SCHEDULER_SETTINGS);
export function resolveSchedulerConfig(stored: unknown): SchedulerConfig {
  return resolveSection(SCHEDULER_SETTINGS, stored);
}

/** Agent-loop settings (`agent`). */
export type AgentConfig = SectionValues<typeof AGENT_SETTINGS>;
export const DEFAULT_AGENT_CONFIG: AgentConfig = defaultsOf(AGENT_SETTINGS);
export function resolveAgentConfig(stored: unknown): AgentConfig {
  return resolveSection(AGENT_SETTINGS, stored);
}

/** Browser session pool settings (`browser`). idleTimeoutMs is ms; Settings shows minutes. */
export type BrowserConfig = SectionValues<typeof BROWSER_SETTINGS>;
export const DEFAULT_BROWSER_CONFIG: BrowserConfig = defaultsOf(BROWSER_SETTINGS);
export function resolveBrowserConfig(stored: unknown): BrowserConfig {
  return resolveSection(BROWSER_SETTINGS, stored);
}

/**
 * Runtime auto-reap for `run_in_background` shells. A shell the agent forgot to
 * kill_shell would otherwise run the whole app lifetime (dev server, watcher);
 * the reaper sweeps and SIGKILLs any background shell that has been idle — no new
 * output AND no bash_output read — for longer than the idle threshold. Only
 * ABANDONED shells are hit: one still producing output or being polled keeps
 * resetting its activity clock. Mirrors the browser pool's idle-sweep pattern.
 */
export const BACKGROUND_SHELL_IDLE_REAP_MS = 20 * 60_000; // 20 min idle → reap
export const BACKGROUND_SHELL_REAP_INTERVAL_MS = 60_000; // sweep cadence: 1 min

/** Retrieval-depth settings (`retrieval`) — how much search scans and returns. */
export type RetrievalConfig = SectionValues<typeof RETRIEVAL_SETTINGS>;
export const DEFAULT_RETRIEVAL_CONFIG: RetrievalConfig = defaultsOf(RETRIEVAL_SETTINGS);
export function resolveRetrievalConfig(stored: unknown): RetrievalConfig {
  return resolveSection(RETRIEVAL_SETTINGS, stored);
}

/** Tool-I/O log settings (`toolIo`). */
export type ToolIoConfig = SectionValues<typeof TOOL_IO_SETTINGS>;
export const DEFAULT_TOOL_IO_CONFIG: ToolIoConfig = defaultsOf(TOOL_IO_SETTINGS);
export function resolveToolIoConfig(stored: unknown): ToolIoConfig {
  return resolveSection(TOOL_IO_SETTINGS, stored);
}

/** External MCP client settings (`mcpClient`) — reconnect backoff, server-instructions cap. */
export type McpClientConfig = SectionValues<typeof MCP_CLIENT_SETTINGS>;
export const DEFAULT_MCP_CLIENT_CONFIG: McpClientConfig = defaultsOf(MCP_CLIENT_SETTINGS);
export function resolveMcpClientConfig(stored: unknown): McpClientConfig {
  return resolveSection(MCP_CLIENT_SETTINGS, stored);
}
